import * as React from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { postMultipartDocumentUpload } from '@/lib/vertex-upload';
import { cn } from '@/lib/utils';

const AADHAAR_ACCEPT = 'image/*,application/pdf,.pdf';

function isPdfUrl(url: string): boolean {
  return /\.pdf($|\?)/i.test(url) || url.includes('/raw/upload/');
}

function isAllowedAadhaarFile(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf')
  );
}

interface FormFileUploadFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  uploadFolder: string;
  accept?: string;
  inputId?: string;
  onUploadingChange?: (uploading: boolean) => void;
}

export const FormFileUploadField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  uploadFolder,
  accept = AADHAAR_ACCEPT,
  inputId,
  onUploadingChange,
}: FormFileUploadFieldProps<TFieldValues>) => {
  const [uploading, setUploading] = React.useState(false);
  const fallbackId = React.useId();
  const fileInputId = inputId || `file-upload-${fallbackId}`;

  React.useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const url = (field.value as string | null | undefined) || '';

        const uploadFile = async (file: File) => {
          if (!isAllowedAadhaarFile(file)) {
            toast.error('Please upload an image or PDF file');
            return;
          }

          setUploading(true);
          try {
            const formData = new FormData();
            const filename =
              file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
                ? file.name.endsWith('.pdf')
                  ? file.name
                  : 'document.pdf'
                : file.name || 'image.webp';
            formData.append('file', file, filename);
            formData.append('filename', filename);
            formData.append('folder', uploadFolder);
            const { url: uploadedUrl } = await postMultipartDocumentUpload(formData, file);
            field.onChange(uploadedUrl);
          } catch (err) {
            console.error(err);
            toast.error(err instanceof Error ? err.message : 'Failed to upload file');
          } finally {
            setUploading(false);
          }
        };

        const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          void uploadFile(file);
          e.target.value = '';
        };

        return (
          <FormItem className={className}>
            <FormLabel>
              {label} {required && <span className="text-primary">*</span>}
            </FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormControl>
              <div className="space-y-3">
                {url ? (
                  <div className="space-y-2">
                    <div className="group relative inline-block max-w-full overflow-hidden rounded-xl border border-border">
                      {isPdfUrl(url) ? (
                        <div className="flex min-h-[120px] min-w-[240px] flex-col items-center justify-center gap-3 bg-muted/30 p-6">
                          <FileText className="h-10 w-10 text-primary" />
                          <span className="text-sm font-medium text-foreground">PDF uploaded</span>
                        </div>
                      ) : (
                        <img
                          src={url}
                          alt="Uploaded"
                          className="block h-auto max-h-80 max-w-full object-contain object-center"
                        />
                      )}
                      {uploading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/85 backdrop-blur-sm">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <span className="px-4 text-center text-sm font-medium text-foreground">
                            Replacing file…
                          </span>
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => field.onChange('')}
                          >
                            <X className="mr-2 h-4 w-4" /> Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className={cn(
                      'rounded-xl border-2 border-dashed border-border p-6 text-center transition-colors',
                      uploading
                        ? 'pointer-events-none border-primary/30 bg-primary/5'
                        : 'hover:bg-accent/10'
                    )}
                  >
                    <input
                      id={fileInputId}
                      type="file"
                      accept={accept}
                      className="hidden"
                      onChange={onFileSelect}
                      disabled={uploading}
                    />
                    <Label
                      htmlFor={fileInputId}
                      className="flex cursor-pointer flex-col items-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm font-semibold text-foreground">
                            Uploading your file…
                          </span>
                          <span className="max-w-[280px] text-xs leading-relaxed text-muted-foreground">
                            Keep this tab open until the upload finishes.
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm font-medium text-primary">Upload file</span>
                          <span className="text-xs text-muted-foreground">
                            Image or PDF (JPG, PNG, PDF)
                          </span>
                        </>
                      )}
                    </Label>
                  </div>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
FormFileUploadField.displayName = 'FormFileUploadField';
